import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Spin, Empty, Tooltip, Switch, Select } from 'antd';
import {
  PrinterOutlined, ReloadOutlined, LoadingOutlined, FileExcelOutlined,
  FlagOutlined, GlobalOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';
import { avecLibelleTraduit } from '../../i18n/libelles';
import TableExportButton from '../../components/TableExportButton';
import PageHeader from '../../components/PageHeader';
import logoLocal from '../../assets/images/logo.png';

// ── Tokens ────────────────────────────────────────────────────────
const G    = '#1a6b3c';
const INK  = '#0f172a';
const MUTED = '#94a3b8';
const BORDER = '#e2e8f0';
const WHITE  = '#ffffff';
const SURFACE = '#f8fafc';

// ── Statuts colonnes (dans l'ordre Excel R5) ─────────────────────
const STATUTS = avecLibelleTraduit([
  { key: 'BON_A_PAYER',    bg: '#dcfce7', color: '#166534' },
  { key: 'ARBITRAGE',      bg: '#fef9c3', color: '#854d0e' },
  { key: 'PROBLEME_RC',    bg: '#fee2e2', color: '#991b1b' },
  { key: 'ATTENTE_OFFRE',  bg: '#dbeafe', color: '#1e3a8a' },
  { key: 'ATTENTE_PIECES', bg: '#f3e8ff', color: '#6b21a8' },
  { key: 'AUTRES',         bg: SURFACE,  color: MUTED    },
], 'statistiques:reclamations.statuts', { cle: 'key' });

// ── Formatage ─────────────────────────────────────────────────────
const fmt = (v) => !v && v !== 0 ? '-'
  : new Intl.NumberFormat(localeIntl()).format(Number(v));

// ── Styles ────────────────────────────────────────────────────────
const thS = (bg = '#1e293b') => ({
  background: bg, color: WHITE, padding: '6px 8px', textAlign: 'center',
  fontSize: 11, fontWeight: 700, border: '1px solid #334155', whiteSpace: 'nowrap',
});
const tdN = (bg = WHITE, bold = false) => ({
  padding: '5px 8px', textAlign: 'right', fontSize: 12,
  border: `1px solid ${BORDER}`, background: bg,
  fontWeight: bold ? 700 : 400, color: bold ? INK : '#334155',
  fontFamily: 'var(--font-main)',
});
const tdL = (bold = false, bg = WHITE) => ({
  ...tdN(bg, bold), textAlign: 'left', fontFamily: "'DM Sans', sans-serif",
  paddingLeft: 10, minWidth: 130,
});

// ── Aplatir un état vers des lignes exportables ───────────────────
function flattenEtat(etat, modeNb) {
  if (!etat?.parPays) return [];
  const rows = [];
  for (const bloc of etat.parPays) {
    for (const l of bloc.lignes) {
      const row = {
        pays: bloc.pays,
        compagnie: l.compagnie,
        total: modeNb ? (l.nbTotal || 0) : Number(l.montantTotal || 0),
      };
      for (const s of STATUTS) {
        row[s.key] = modeNb
          ? (l.nbParStatut?.[s.key] || 0)
          : Number(l.montantParStatut?.[s.key] || 0);
      }
      rows.push(row);
    }
    const tp = bloc.totalPays;
    const totalRow = {
      pays: bloc.pays,
      compagnie: `TOTAL ${bloc.pays}`,
      total: modeNb ? (tp.nbTotal || 0) : Number(tp.montantTotal || 0),
    };
    for (const s of STATUTS) {
      totalRow[s.key] = modeNb
        ? (tp.nbParStatut?.[s.key] || 0)
        : Number(tp.montantParStatut?.[s.key] || 0);
    }
    rows.push(totalRow);
  }
  return rows;
}

// ── Colonnes d'export ──────────────────────────────────────────────
function colsReclamationsExport(modeNb, t) {
  const base = [
    { title: t('reclamations.colonnes.pays'), dataIndex: 'pays' },
    { title: t('reclamations.colonnes.compagnieMembre'), dataIndex: 'compagnie' },
    modeNb
      ? { title: t('reclamations.colonnes.totalDossiers'), dataIndex: 'total', numeric: true }
      : { title: t('reclamations.colonnes.totalFcfa'), dataIndex: 'total', format: 'currency' },
  ];
  const statuts = STATUTS.map(s => modeNb
    ? { title: s.label, dataIndex: s.key, numeric: true }
    : { title: s.label, dataIndex: s.key, format: 'currency' }
  );
  return [...base, ...statuts];
}

// ══════════════════════════════════════════════════════════════════
//  PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════
export default function EtatReclamations() {
  const { t } = useTranslation('statistiques');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [modeNb,  setModeNb]  = useState(true);  // true = nombre, false = montant
  const [activeTab, setActiveTab] = useState('togo-homologues');
  const [exercices, setExercices] = useState([]);      // années disponibles
  const [exercice,  setExercice]  = useState(null);    // null = tous exercices
  const [exporting, setExporting] = useState(false);

  // Charger la liste des exercices une seule fois (défaut = le plus récent)
  useEffect(() => {
    axiosInstance.get('/v1/stats/reclamations/exercices')
      .then(r => {
        const list = r.data?.data || [];
        setExercices(list);
        if (list.length) setExercice(list[0]);
      })
      .catch(() => {});
  }, []);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/v1/stats/reclamations', {
        params: exercice ? { exercice } : {},
      });
      setData(r.data?.data);
    } catch { toast.error(t('reclamations.erreurChargement')); }
    setLoading(false);
  }, [exercice, t]);

  useEffect(() => { charger(); }, [charger]);

  // Export Excel "Suspens détail" généré par le backend (Apache POI)
  const exporterExcel = useCallback(async () => {
    setExporting(true);
    try {
      const sens = activeTab === 'togo-homologues'
        ? 'TOGO_VERS_HOMOLOGUES' : 'HOMOLOGUES_VERS_TOGO';
      const r = await axiosInstance.get('/v1/stats/reclamations/export', {
        params: { sens, ...(exercice ? { exercice } : {}) },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Suspens_detail${exercice ? '_' + exercice : ''}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error(t('reclamations.erreurExportExcel')); }
    setExporting(false);
  }, [activeTab, exercice, t]);

  // Compte : uniquement les lignes « compagnie » de l'onglet visible ; les lignes
  // « TOTAL <pays> » et le total general sont des syntheses, pas des donnees.
  const etatActif = activeTab === 'togo-homologues'
    ? data?.togoVersHomologues
    : data?.homologuesVersTogo;
  const nbLignesAffichees = (etatActif?.parPays || [])
    .reduce((n, bloc) => n + (bloc.lignes?.length || 0), 0);

  const tabItems = [
    {
      key: 'togo-homologues',
      label: <span><FlagOutlined /> {t('reclamations.ongletTogoHomologues')}</span>,
      children: data
        ? <BlocEtat etat={data.togoVersHomologues} modeNb={modeNb} sens={t('reclamations.compagnieHomologue')} />
        : null,
    },
    {
      key: 'homologues-togo',
      label: <span><GlobalOutlined /> {t('reclamations.ongletHomologuesTogo')}</span>,
      children: data
        ? <BlocEtat etat={data.homologuesVersTogo} modeNb={modeNb} sens={t('reclamations.compagnieMembre')} />
        : null,
    },
  ];

  return (
    <Layout title={t('reclamations.titreOnglet')}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        .recl * { font-family: 'DM Sans', sans-serif; }
        .recl table { border-collapse: collapse; width: 100%; }
        .recl tr:hover td { filter: brightness(0.97); }
        .recl .pays-row td { background: #f1f5f9 !important; font-weight: 700 !important; }
        .recl .total-pays td { background: #e2e8f0 !important; font-weight: 700 !important; }
        .recl .total-global td { background: #1e293b !important; color: #fff !important; font-weight: 700 !important; }
        @media print { .no-print { display: none !important; } }
      `}</style>

      <div className="recl" style={{ maxWidth: 1400 }}>

        {/* ── En-tête ── */}
        <PageHeader
          titre={t('reclamations.titre')}
          icone={<FlagOutlined />}
          compte={data ? nbLignesAffichees : undefined}
          unite={t('reclamations.unite')}
          precision={data?.dateArrete ? t('commun.situationAu', { date: data.dateArrete }) : undefined}
          actions={(
            <div className="no-print"
              style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>

              {/* Sélecteur d'exercice */}
              <Select
                value={exercice ?? 'ALL'}
                onChange={v => setExercice(v === 'ALL' ? null : v)}
                style={{ minWidth: 150 }}
                options={[
                  { value: 'ALL', label: t('reclamations.tousExercices') },
                  ...exercices.map(a => ({ value: a, label: t('reclamations.exerciceAnnee', { annee: a }) })),
                ]}
              />

              {/* Toggle Nombre / Montant */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                background: SURFACE, border: `1px solid ${BORDER}`,
                borderRadius: 8, padding: '6px 14px' }}>
                <span style={{ fontSize: 12, color: modeNb ? INK : MUTED, fontWeight: modeNb ? 600 : 400 }}>
                  {t('reclamations.nombre')}
                </span>
                <Switch
                  checked={!modeNb}
                  onChange={v => setModeNb(!v)}
                  style={{ background: !modeNb ? G : '#cbd5e1' }}
                />
                <span style={{ fontSize: 12, color: !modeNb ? INK : MUTED, fontWeight: !modeNb ? 600 : 400 }}>
                  {t('reclamations.montantFcfa')}
                </span>
              </div>

              <Tooltip title={t('commun.actualiser')}>
                <button onClick={charger} style={{ border: `1px solid ${BORDER}`,
                  borderRadius: 8, padding: '7px 12px', background: WHITE,
                  cursor: 'pointer', color: MUTED }}>
                  <ReloadOutlined />
                </button>
              </Tooltip>
              <TableExportButton
                floating
                data={data ? flattenEtat(
                  activeTab === 'togo-homologues'
                    ? data.togoVersHomologues
                    : data.homologuesVersTogo,
                  modeNb
                ) : []}
                columns={colsReclamationsExport(modeNb, t)}
                filename={`etat-reclamations-${activeTab === 'togo-homologues' ? 'togo-vers-homologues' : 'homologues-vers-togo'}-${modeNb ? 'nombre' : 'montant'}`}
                title={activeTab === 'togo-homologues'
                  ? t('reclamations.export.titreTogoHomologues')
                  : t('reclamations.export.titreHomologuesTogo')}
                subtitle={t('reclamations.export.sousTitre', {
                  mode: modeNb ? t('reclamations.export.nombreDossiers') : t('reclamations.export.montantsFcfa'),
                  date: data?.dateArrete || '-',
                })}
                logo={logoLocal}
                showTotals={false}
                disabled={!data}
              />
              <Tooltip title={t('reclamations.aideSuspensDetail')}>
                <button onClick={exporterExcel} disabled={!data || exporting} style={{
                  border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 16px',
                  fontSize: 13, background: G, color: WHITE, fontWeight: 600,
                  cursor: data && !exporting ? 'pointer' : 'not-allowed',
                  display: 'flex', gap: 6, alignItems: 'center' }}>
                  <FileExcelOutlined /> {t('reclamations.suspensDetail')}
                </button>
              </Tooltip>
              <button onClick={() => window.print()} disabled={!data} style={{
                border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 16px',
                fontSize: 13, background: WHITE, color: INK, fontWeight: 600,
                cursor: data ? 'pointer' : 'not-allowed',
                display: 'flex', gap: 6, alignItems: 'center' }}>
                <PrinterOutlined /> {t('reclamations.imprimer')}
              </button>
            </div>
          )}
        />

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: G }} />} />
          </div>
        ) : !data ? (
          <Empty description={t('commun.aucuneDonnee')} />
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            type="card"
          />
        )}
      </div>
    </Layout>
  );
}

// ── Bloc d'un état (Togo→Homologues ou Homologues→Togo) ───────────
function BlocEtat({ etat, modeNb, sens }) {
  const { t } = useTranslation('statistiques');
  if (!etat?.parPays?.length)
    return <Empty description={t('reclamations.aucunDossierActif')} style={{ padding: 40 }} />;

  return (
    <div>
      {etat.parPays.map((bloc, i) => (
        <div key={i} style={{ marginBottom: 24,
          border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
          <BlocPays bloc={bloc} modeNb={modeNb} sens={sens}
            totalGlobal={etat.total} />
        </div>
      ))}

      {/* Total général */}
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ background: '#1e293b', color: WHITE, padding: '10px 16px',
          fontWeight: 700, fontSize: 13 }}>
          {t('reclamations.totalGeneralTousPays')}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <TableReclamation
            lignes={[etat.total]}
            getLabelFn={l => t('reclamations.totalGeneral')}
            modeNb={modeNb}
            isGlobal
          />
        </div>
      </div>
    </div>
  );
}

// ── Bloc d'un pays ────────────────────────────────────────────────
function BlocPays({ bloc, modeNb, sens, totalGlobal }) {
  const { t } = useTranslation('statistiques');
  const pct = (part, total) =>
    total > 0 ? ((Number(part) / Number(total)) * 100).toFixed(1) + '%' : '-';

  return (
    <>
      <div style={{ background: '#334155', color: WHITE, padding: '8px 14px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>{bloc.pays}</span>
        <span style={{ fontSize: 11, opacity: 0.7 }}>
          {t('reclamations.nbDossiers', { count: bloc.totalPays.nbTotal })}
          {!modeNb && ` · ${fmt(bloc.totalPays.montantTotal)} FCFA`}
          {' '}({pct(
            modeNb ? bloc.totalPays.nbTotal : bloc.totalPays.montantTotal,
            modeNb ? totalGlobal.nbTotal   : totalGlobal.montantTotal
          )})
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <TableReclamation
          lignes={bloc.lignes}
          totalPays={bloc.totalPays}
          getLabelFn={l => l.compagnie}
          modeNb={modeNb}
          sens={sens}
        />
      </div>
    </>
  );
}

// ── Tableau d'un bloc ─────────────────────────────────────────────
function TableReclamation({ lignes, totalPays, getLabelFn, modeNb, sens, isGlobal }) {
  const { t } = useTranslation('statistiques');
  const val = (l, statKey) => modeNb
    ? (l.nbParStatut?.[statKey] || 0)
    : Number(l.montantParStatut?.[statKey] || 0);
  const tot = (l) => modeNb ? l.nbTotal : Number(l.montantTotal);

  return (
    <table>
      <thead>
        <tr>
          <th style={{ ...thS('#475569'), minWidth: 140, textAlign: 'left', paddingLeft: 10 }}>
            {sens || t('reclamations.compagnieMajuscules')}
          </th>
          <th style={{ ...thS('#166534'), minWidth: 90 }}>
            {modeNb ? t('reclamations.totalDossiersMajuscules') : t('reclamations.totalFcfaMajuscules')}
          </th>
          {STATUTS.map(s => (
            <th key={s.key} style={{ ...thS(s.color), minWidth: 100 }}>
              {s.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lignes.map((l, i) => (
          <tr key={i}>
            <td style={tdL(false, i % 2 === 0 ? WHITE : SURFACE)}>{getLabelFn(l)}</td>
            <td style={tdN('#f0fdf4', true)}>
              {modeNb ? fmt(l.nbTotal) : fmt(l.montantTotal)}
            </td>
            {STATUTS.map(s => {
              const v = val(l, s.key);
              return (
                <td key={s.key} style={tdN(v > 0 ? s.bg : WHITE)}>
                  {v > 0 ? fmt(v) : <span style={{ color: MUTED }}>-</span>}
                </td>
              );
            })}
          </tr>
        ))}

        {totalPays && (
          <tr className="total-pays">
            <td style={tdL(true, '#e2e8f0')}>TOTAL {totalPays.pays || ''}</td>
            <td style={tdN('#86efac', true)}>
              {modeNb ? fmt(totalPays.nbTotal) : fmt(totalPays.montantTotal)}
            </td>
            {STATUTS.map(s => (
              <td key={s.key} style={tdN('#e2e8f0', true)}>
                {modeNb
                  ? fmt(totalPays.nbParStatut?.[s.key] || 0)
                  : fmt(totalPays.montantParStatut?.[s.key] || 0)}
              </td>
            ))}
          </tr>
        )}

        {isGlobal && (
          <tr className="total-global">
            <td style={{ ...tdL(true, '#1e293b'), color: WHITE }}>{t('reclamations.totalGeneral')}</td>
            <td style={{ ...tdN('#166534', true), color: WHITE }}>
              {modeNb ? fmt(lignes[0].nbTotal) : fmt(lignes[0].montantTotal)}
            </td>
            {STATUTS.map(s => (
              <td key={s.key} style={{ ...tdN('#1e293b', true), color: WHITE }}>
                {modeNb
                  ? fmt(lignes[0].nbParStatut?.[s.key] || 0)
                  : fmt(lignes[0].montantParStatut?.[s.key] || 0)}
              </td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  );
}
